import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '../features/auth/useAuth';
import MiniAvatarCanvas from './MiniAvatarCanvas';
import ThemeToggle from './ThemeToggle';

const AVATAR_ICONS: Record<string, string> = {
  default: '',
  cat: '\uD83D\uDC31',
  dog: '\uD83D\uDC36',
  fox: '\uD83E\uDD8A',
  owl: '\uD83E\uDD89',
  bear: '\uD83D\uDC3B',
  wolf: '\uD83D\uDC3A',
  eagle: '\uD83E\uDD85',
  lion: '\uD83E\uDD81',
  panda: '\uD83D\uDC3C',
  robot: '\uD83E\uDD16',
  skull: '\uD83D\uDC80',
  ghost: '\uD83D\uDC7B',
  alien: '\uD83D\uDC7D',
  wizard: '\uD83E\uDDD9',
};

export function avatarContent(user: User): string {
  const icon = AVATAR_ICONS[user.avatar];
  if (icon) return icon;
  return user.username.charAt(0).toUpperCase();
}

export { AVATAR_ICONS };

interface HeaderDropdownProps {
  user: User;
  onLogout: () => void;
  logoutPending: boolean;
}

export default function HeaderDropdown({ user, onLogout, logoutPending }: HeaderDropdownProps) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const close = useCallback(() => setOpen(false), []);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, close]);

  const display = avatarContent(user);
  const isCustomAvatar = user.avatar === 'custom' && user.avatar_config;

  return (
    <div className="malpedia-header-dropdown" ref={containerRef}>
      <button
        className="malpedia-header-dropdown-trigger"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {isCustomAvatar ? (
          <MiniAvatarCanvas config={user.avatar_config!} size={28} />
        ) : (
          <span className="malpedia-avatar-circle">{display}</span>
        )}
        <span className="malpedia-app-user">{user.username}</span>
      </button>

      {open && (
        <div className="malpedia-header-dropdown-menu" role="menu">
          <button
            className="malpedia-header-dropdown-item"
            role="menuitem"
            onClick={() => { close(); navigate('/profile'); }}
          >
            Profile
          </button>
          <button
            className="malpedia-header-dropdown-item"
            role="menuitem"
            onClick={() => { close(); navigate('/avatars'); }}
          >
            My Avatars
          </button>
          <button
            className="malpedia-header-dropdown-item"
            role="menuitem"
            onClick={() => { close(); navigate('/settings'); }}
          >
            Settings
          </button>
          <div className="malpedia-header-dropdown-divider" />
          <ThemeToggle />
          <div className="malpedia-header-dropdown-divider" />
          <button
            className="malpedia-header-dropdown-item malpedia-header-dropdown-item--danger"
            role="menuitem"
            onClick={() => { close(); onLogout(); }}
            disabled={logoutPending}
          >
            {logoutPending ? 'Signing out\u2026' : 'Sign out'}
          </button>
        </div>
      )}
    </div>
  );
}
